import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { DarkModeToggle } from "../components/DarkModeToggle";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Download, Eye, Headphones, BookOpen,
  Hand, ListOrdered, Repeat, Shuffle, Check, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STYLE_ICONS = {
  eye: Eye, headphones: Headphones, "book-open": BookOpen,
  "hand-metal": Hand, "list-ordered": ListOrdered, repeat: Repeat, shuffle: Shuffle,
};

export default function HowILearn() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { styleId } = useParams();

  const [styles, setStyles] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  // P1 Feb 28, 2026 — multi-select preferences (Understanding-
  // before-routing doctrine). Stored as an array; the legacy
  // single-string field stays in sync via the backend (first
  // entry mirrors `learning_preference`).
  const [myPrefs, setMyPrefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (styleId) {
          const { data } = await axios.get(`${API}/api/learning/styles/${styleId}`, { withCredentials: true });
          setSelectedStyle(data);
        } else {
          const [stylesRes, prefRes] = await Promise.all([
            axios.get(`${API}/api/learning/styles`, { withCredentials: true }),
            axios.get(`${API}/api/learning/my-preferences`, { withCredentials: true }).catch(() => ({ data: { style_ids: [] } })),
          ]);
          setStyles(stylesRes.data.styles || []);
          setMyPrefs(Array.isArray(prefRes.data.style_ids) ? prefRes.data.style_ids : []);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [styleId]);

  const togglePreference = async (id) => {
    const next = myPrefs.includes(id)
      ? myPrefs.filter((s) => s !== id)
      : [...myPrefs, id];
    setMyPrefs(next);
    try {
      await axios.post(
        `${API}/api/learning/my-preferences`,
        { style_ids: next },
        { withCredentials: true },
      );
      toast.success(
        next.includes(id)
          ? "Added to how you learn"
          : "Removed from how you learn",
      );
    } catch {
      // Revert on failure so the UI never shows a state the
      // server doesn't have.
      setMyPrefs(myPrefs);
      toast.error("Couldn't save — please try again");
    }
  };

  const trackDownload = async (sid, rid, title) => {
    // Honest download — fetch the real PDF as a blob, click an <a download>,
    // and only toast success when the file actually starts saving.
    try {
      const res = await fetch(`${API}/api/learning/resource/${sid}/${rid}.pdf`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        throw new Error("Empty file");
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ndg-${sid}-${rid}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${title || "Resource"} downloaded`);
    } catch (err) {
      toast.error(`Couldn't download — ${err?.message || "please try again"}`);
    }
  };

  if (loading) return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>
  );

  // ── STYLE DETAIL VIEW ──
  if (selectedStyle) {
    const Icon = STYLE_ICONS[selectedStyle.icon] || Eye;
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-8">
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <button data-testid="back-btn" onClick={() => nav("/learn/styles")} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
              <ChevronLeft className="w-5 h-5 text-slate-500" />
            </button>
            <h1 className="text-lg font-extrabold text-slate-900 flex-1">{selectedStyle.title}</h1>
            <DarkModeToggle />
          </div>
        </header>

        <div className="max-w-lg mx-auto px-4 mt-5 space-y-4">
          {/* Style header */}
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: selectedStyle.color + "20" }}>
              <Icon className="w-8 h-8" style={{ color: selectedStyle.color }} />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900">{selectedStyle.title}</h2>
            <p className="text-sm text-slate-400 mt-1">{selectedStyle.subtitle}</p>
            {!myPrefs.includes(selectedStyle.id) && (
              <button data-testid="set-preference-btn" onClick={() => togglePreference(selectedStyle.id)}
                className="mt-3 text-sm font-bold text-blue-600 hover:text-blue-700">Add to how I learn</button>
            )}
            {myPrefs.includes(selectedStyle.id) && (
              <button data-testid="unset-preference-btn" onClick={() => togglePreference(selectedStyle.id)}
                className="mt-3 text-sm font-bold text-emerald-500 flex items-center justify-center gap-1 mx-auto">
                <Check className="w-4 h-4" />Part of how you learn · tap to remove
              </button>
            )}
          </div>

          {/* Resources */}
          <div className="space-y-3">
            <h3 className="text-base font-extrabold text-slate-900">Resources</h3>
            {(selectedStyle.resources || []).map(res => (
              <div key={res.id} data-testid={`resource-${res.id}`}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: selectedStyle.color + "15" }}>
                    {res.downloadable ? <Download className="w-5 h-5" style={{ color: selectedStyle.color }} /> : <Icon className="w-5 h-5" style={{ color: selectedStyle.color }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{res.title}</p>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{res.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500">{res.type}</span>
                      {res.downloadable && (
                        <button data-testid={`download-${res.id}`}
                          onClick={() => trackDownload(selectedStyle.id, res.id, res.title)}
                          className="text-[10px] font-bold text-blue-600 flex items-center gap-0.5 hover:text-blue-700">
                          <Download className="w-3 h-3" />Download
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── STYLES LIST VIEW ──
  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => nav("/learn")} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <h1 className="text-lg font-extrabold text-slate-900 flex-1">How I Learn Best</h1>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 mt-5 space-y-4">
        <div className="text-center py-2">
          <h2 className="text-2xl font-extrabold text-slate-900">How you learn best</h2>
          <p className="text-sm text-slate-400 mt-1">Pick everything that helps — you can choose more than one.</p>
        </div>

        <div className="space-y-3">
          {styles.map(style => {
            const Icon = STYLE_ICONS[style.icon] || Eye;
            const isActive = myPrefs.includes(style.id);
            return (
              <button key={style.id} data-testid={`style-${style.id}`}
                onClick={() => nav(`/learn/styles/${style.id}`)}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all active:scale-[0.97]
                  ${isActive
                    ? "bg-white dark:bg-slate-800 border-2 shadow-lg"
                    : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                  }`}
                style={isActive ? { borderColor: style.color } : {}}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: style.color + "15" }}>
                  <Icon className="w-6 h-6" style={{ color: style.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-bold text-slate-900">{style.title}</p>
                    {isActive && <Check className="w-4 h-4 text-emerald-500" />}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{style.subtitle}</p>
                  <p className="text-[10px] text-slate-300 mt-1">{style.resource_count} resources</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
